import React, { useCallback, useMemo } from 'react';
import { WorkflowHiringReadinessScoreWriteBackStrategy } from 'libs/end-to-end/src/types/databases/workflows/hiring-readiness-score-write-back/workflow-hiring-readiness-score-write-back';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { ConfigureWriteBackGreenhouseStrategy } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackGreenhouseStrategy';
import { ConfigureWorkflowBaseProps } from 'apps/sigma2-ui/src/components/workflows/types';
import { ConfigureWriteBackWorkdayViaWsStrategy } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackWorkdayViaWsStrategy';
import { ConfigureWriteBackLeverStrategy } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackLeverStrategy';
import { ConfigureWriteBackFountainStrategy } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackFountainStrategy';
import { ConfigureWriteBackAshbyStrategy } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackAshbyStrategy';

export interface ConfigureHiringReadinessScoreWriteBackStrategyProps
  extends ConfigureWorkflowBaseProps {
  strategy: WorkflowHiringReadinessScoreWriteBackStrategy | null;

  updateStrategy: (
    strategy: WorkflowHiringReadinessScoreWriteBackStrategy | null,
  ) => void;
}

export const ConfigureHiringReadinessScoreWriteBackStrategy = ({
  strategy,
  updateStrategy,
  listSecretsPattern,
  organizationId,
  listDataSetsPattern,
}: ConfigureHiringReadinessScoreWriteBackStrategyProps) => {
  const setSelectedType = useCallback(
    (
      selected: 'none' | WorkflowHiringReadinessScoreWriteBackStrategy['type'],
    ) => {
      if (selected === 'none') {
        updateStrategy(null);
      } else if (selected !== strategy?.type) {
        updateStrategy({
          type: selected,
        });
      }
    },
    [strategy, updateStrategy],
  );

  const selectedType = useMemo(() => {
    return strategy?.type ?? 'none';
  }, [strategy]);

  return (
    <div>
      <SelectBoxWithSearch
        type="single"
        list={[
          { optionLabel: 'None', optionValue: 'none' },
          { optionLabel: 'Greenhouse', optionValue: 'greenhouse' },
          { optionLabel: 'Lever', optionValue: 'lever' },
          { optionLabel: 'Workday', optionValue: 'workdayViaWs' },
          { optionLabel: 'Fountain', optionValue: 'fountain' },
          { optionLabel: 'ADP', optionValue: 'adp' },
          { optionLabel: 'Ashby', optionValue: 'ashby' },
        ]}
        label="Select a strategy"
        selected={selectedType}
        setSelected={setSelectedType}
      />

      {strategy?.type === 'greenhouse' && (
        <ConfigureWriteBackGreenhouseStrategy
          strategy={strategy}
          updateStrategy={updateStrategy}
          listSecretsPattern={listSecretsPattern}
          organizationId={organizationId}
          listDataSetsPattern={listDataSetsPattern}
        />
      )}

      {strategy?.type === 'workdayViaWs' && (
        <ConfigureWriteBackWorkdayViaWsStrategy
          strategy={strategy}
          updateStrategy={updateStrategy}
          listSecretsPattern={listSecretsPattern}
          organizationId={organizationId}
          listDataSetsPattern={listDataSetsPattern}
        />
      )}

      {strategy?.type === 'lever' && (
        <ConfigureWriteBackLeverStrategy
          strategy={strategy}
          updateStrategy={updateStrategy}
          listSecretsPattern={listSecretsPattern}
          organizationId={organizationId}
          listDataSetsPattern={listDataSetsPattern}
        />
      )}

      {strategy?.type === 'fountain' && (
        <ConfigureWriteBackFountainStrategy
          strategy={strategy}
          updateStrategy={updateStrategy}
          listSecretsPattern={listSecretsPattern}
          organizationId={organizationId}
          listDataSetsPattern={listDataSetsPattern}
          allowedPlacesToWriteTypes={['customField']}
        />
      )}

      {strategy?.type === 'ashby' && (
        <ConfigureWriteBackAshbyStrategy
          strategy={strategy}
          updateStrategy={updateStrategy}
          listSecretsPattern={listSecretsPattern}
          organizationId={organizationId}
          listDataSetsPattern={listDataSetsPattern}
        />
      )}
    </div>
  );
};
